import * as React from 'react';
import {FormHelperText, Box, RadioGroup, Radio as RadioEl, FormControlLabel, FormLabel, FormControl} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useMemo, memo } from 'react';

interface RadioProps {
    onClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    enums: string[];
    label?: string;
    description?: string;
    scope?: string;
    required?: boolean;
    value: string;
    error?: string;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Radio = memo((props: RadioProps) => {
    const {
        onClick,
        enums,
        label,
        description,
        scope,
        required = false,
        value = '',
        error,
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && !value,
        [required, appHasBeenValidated, value]
    );

    const showError = useMemo(() =>
            error !== undefined &&
            error !== '' &&
            error !== "must be equal to one of the allowed values",
        [error]
    );

    const hasError = showError || showRequiredError;
    const errorMessage = showRequiredError ? 'This field is required' : error;

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const radioOptions = useMemo(() =>
            enums
                .filter((el: string) => {
                    // Remove empty, null, undefined, and whitespace-only strings
                    return el != null && el.trim() !== "";
                })
                .map((el: string) => (
                    <FormControlLabel
                        key={el}
                        value={el}
                        disabled={!enabled}
                        control={
                            <RadioEl
                                size="small"
                                color="secondary"
                                icon={<RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                            />
                        }
                        label={el}
                    />
                )),
        [enums, enabled]
    );


    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control">
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <RadioGroup
                    aria-label={scope}
                    value={value}
                    name={scope}
                    onChange={onClick}
                >
                    {radioOptions}
                </RadioGroup>
                {showDescription && (
                    <FormHelperText>{description}</FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>{errorMessage}</FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Radio.displayName = 'Radio';

export default Radio;
